import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { tg, isInTelegram } from '@/lib/telegram'
import type { TgWidgetUser } from '@/lib/telegram-widget'
import { api } from '@/lib/api'
import type { User } from '@/types'

const STORAGE_TOKEN = 'sv:jwt'
const STORAGE_USER  = 'sv:user'
const USE_MOCK = (import.meta.env.VITE_USE_MOCK ?? 'false') === 'true'

/** อ่าน token + user จาก sessionStorage ตอน init (seamless กลับเข้าใช้งานทันที) */
function loadFromStorage(): { token: string | null; user: User | null } {
  try {
    const t = sessionStorage.getItem(STORAGE_TOKEN)
    const u = sessionStorage.getItem(STORAGE_USER)
    return {
      token: t,
      user: u ? (JSON.parse(u) as User) : null,
    }
  } catch {
    return { token: null, user: null }
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage()
  const token   = ref<string | null>(initial.token)
  const user    = ref<User | null>(initial.user)
  const loading = ref(false)
  const error   = ref<string | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value && user.value))

  // ----- Persist to sessionStorage ทุกครั้งที่เปลี่ยน -----
  watch(token, (v) => {
    if (v) sessionStorage.setItem(STORAGE_TOKEN, v)
    else sessionStorage.removeItem(STORAGE_TOKEN)
  })
  watch(user, (v) => {
    if (v) sessionStorage.setItem(STORAGE_USER, JSON.stringify(v))
    else sessionStorage.removeItem(STORAGE_USER)
  }, { deep: true })

  /** เรียกตอน app boot — ทำงานเฉพาะใน Telegram Mini App (browser ใช้ Login Widget จาก LoginView) */
  async function ensureAuth(): Promise<void> {
    if (isAuthenticated.value) return
    if (!isInTelegram() && !USE_MOCK) return
    await loginWithTelegram()
  }

  /**
   * Mini App login: ใช้ initData จริงจาก Telegram WebApp
   *   - VITE_USE_MOCK=true → mock user ในเครื่อง ไม่ยิง backend
   *   - ใน Mini App → ส่ง initData → backend verify HMAC
   *   - ไม่อยู่ใน Telegram → throw error (browser ต้องใช้ loginWithWidget แทน)
   */
  async function loginWithTelegram(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      // Mode 1: pure mock
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 300))
        user.value  = {
          id: 1, firstName: 'สังข์ทอง', lastName: 'รัตน์ภิรมย์',
          username: 'sangthong_invest', languageCode: 'th',
        }
        token.value = 'mock-jwt-token'
        return
      }

      // Mode 2: ยิง backend จริง — ต้องอยู่ใน Telegram Mini App
      if (!isInTelegram()) {
        throw new Error('ต้องเข้าผ่าน Telegram Mini App หรือใช้ Login Widget')
      }
      const initData = tg()?.initData ?? ''
      const { data } = await api.post('/api/auth/telegram', { initData })
      token.value = data.token
      user.value  = data.user
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e as Error)?.message ??
        'เข้าสู่ระบบไม่สำเร็จ'
      error.value = msg
      // เคลียร์ของเก่าถ้าเข้าไม่สำเร็จ
      token.value = null
      user.value  = null
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Dev-only login — ใช้ตอน local dev เมื่อ Widget ไม่ทำงาน (ยังไม่ได้ /setdomain)
   * Backend ต้องตั้ง ALLOW_DEV_LOGIN=true ใน .dev.vars (default ใน production คือ false)
   * ปุ่มนี้ tree-shake ออกตอน vite build (import.meta.env.DEV → false)
   */
  async function loginDev(): Promise<void> {
    if (!import.meta.env.DEV) throw new Error('dev login disabled in production build')
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/api/auth/telegram', {})
      token.value = data.token
      user.value  = data.user
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e as Error)?.message ??
        'dev login ไม่สำเร็จ'
      error.value = msg
      throw e
    } finally {
      loading.value = false
    }
  }

  /** Login Widget callback (เว็บไซต์ปกติ ไม่ใช่ Mini App) */
  async function loginWithWidget(widgetUser: TgWidgetUser): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/api/auth/telegram', widgetUser)
      token.value = data.token
      user.value  = data.user
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e as Error)?.message ??
        'login widget ไม่สำเร็จ'
      error.value = msg
      throw e
    } finally {
      loading.value = false
    }
  }

  function logout() {
    token.value = null
    user.value  = null
    error.value = null
  }

  /** อ่าน Telegram user hint จาก initDataUnsafe (ก่อน backend verify) */
  function readTelegramUserHint(): User | null {
    if (!isInTelegram()) return null
    const u = tg()?.initDataUnsafe?.user
    if (!u) return null
    return {
      id: u.id, firstName: u.first_name, lastName: u.last_name,
      username: u.username, languageCode: u.language_code,
      isPremium: u.is_premium, photoUrl: u.photo_url,
    }
  }

  return {
    token, user, loading, error, isAuthenticated,
    ensureAuth, loginWithTelegram, loginWithWidget, loginDev, logout, readTelegramUserHint,
  }
})
